import { useEffect, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import type { LaunchCatalogRow } from '../lib/data';
import { useLaunchCatalog } from '../lib/mutations';
import { parseShippedLaunchSpecs, type ShippedLaunchSpec } from '../lib/launch-policy';

export function LaunchPolicy({ client, guard, orgId, rows, onChanged }: {
  client: SupabaseClient; guard: Guard; orgId: string;
  rows: readonly LaunchCatalogRow[]; onChanged: () => Promise<void>;
}) {
  const [defaults, setDefaults] = useState<readonly ShippedLaunchSpec[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { setSpec, clearSpec, busy, error } = useLaunchCatalog(client);

  useEffect(() => {
    let live = true;
    void fetch('/node/catalog/launch.json', { cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) throw new Error('unavailable');
        const parsed = parseShippedLaunchSpecs(await response.json());
        if (parsed === null) throw new Error('invalid');
        if (live) { setDefaults(parsed); setLoadError(null); }
      })
      .catch(() => { if (live) setLoadError('The verified launch catalog is not available from Command yet.'); });
    return () => { live = false; };
  }, []);

  const publishDefaults = () => {
    if (defaults === null) return;
    void guard('post_owner_command', async () => {
      for (let i = 0; i < defaults.length; i += 1) {
        const spec = defaults[i]!;
        const ok = await setSpec({
          orgId, runtimeKey: spec.runtimeKey, file: spec.file, args: spec.args,
          needsModel: spec.needsModel, position: (i + 1) * 10,
          ...(spec.env === undefined ? {} : { env: spec.env }),
        });
        if (!ok) return;
      }
      await onChanged();
    });
  };

  const clear = (runtimeKey: string) => void guard('post_owner_command', async () => {
    if (await clearSpec(orgId, runtimeKey)) await onChanged();
  });

  return (
    <div className="stack">
      <small className="muted">
        Central launch policy overrides the matching runtime on every node. A runtime with no central row falls back to the verified file shipped with that node.
      </small>
      {rows.length === 0 ? (
        <p className="muted">No central overrides yet. Every runtime is using its shipped fallback.</p>
      ) : (
        <ul className="list">
          {rows.map((row) => (
            <li key={row.runtime_key} className="row spread">
              <span>
                <strong>{row.runtime_label ?? row.runtime_key}</strong>
                <small className="muted"> · #{row.position} · {row.file} {row.args.join(' ')}</small>
              </span>
              <button type="button" className="link" disabled={busy} onClick={() => clear(row.runtime_key)}>
                Use node fallback
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="row">
        <button type="button" className="primary" disabled={busy || defaults === null} onClick={publishDefaults}>
          Publish verified defaults
        </button>
        {defaults && <small className="muted">{defaults.map((spec) => spec.runtimeKey).join(' → ')}</small>}
      </div>
      {loadError && <p role="alert">{loadError}</p>}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
